import type { AppDefinition } from '@/types/app';
import type { ComponentDefinition } from '@/types/component';
import type { EntitySchema } from '@/types/schema';
import type { ExpressionNode } from '@/types/expression';
import { ASTNodeType } from '@/types/expression';
import type { ValidationError } from './DefinitionValidator';
import { ExpressionEngine } from '@/engine/expression/ExpressionEngine';
import { tokenize } from '@/engine/expression/tokenizer';
import { parse } from '@/engine/expression/parser';
import builtinFunctions from '@/engine/expression/functions';

const expressionEngine = new ExpressionEngine();

// The set of REAL engine builtins, derived from the registry itself (never a hand-copied
// list) so it can't rot as functions.ts grows — this is exactly the object evaluator.ts
// dispatches FunctionCall nodes against (`builtinFunctions[callNode.name]`), so membership
// here is equivalent to "would NOT throw `Unknown function` at runtime".
const BUILTIN_NAMES = new Set(Object.keys(builtinFunctions));

/**
 * Semantic lint — catches mistakes that are structurally valid but render wrong,
 * the class of bug that slips past DefinitionValidator (e.g. a sprite positioned
 * with style bound to state that is never initialized → NaN → invisible; screens
 * nothing can reach). These feed the same auto-retry loop as structural errors.
 */
const STATE_REF = /\$\.state\.([a-zA-Z_]\w*)/g;

function collectStateRefs(value: unknown, out: Set<string>): void {
  if (typeof value === 'string') {
    let m: RegExpExecArray | null;
    STATE_REF.lastIndex = 0;
    while ((m = STATE_REF.exec(value))) out.add(m[1]);
  } else if (Array.isArray(value)) {
    for (const v of value) collectStateRefs(v, out);
  } else if (value && typeof value === 'object') {
    for (const v of Object.values(value)) collectStateRefs(v, out);
  }
}

/** Walk a component subtree, collecting setState keys (written) and state keys read in `style`. */
function walk(
  comp: ComponentDefinition,
  written: Set<string>,
  styleReads: Map<string, string>,
  path: string,
): void {
  // setState keys written anywhere in this component's props (handlers may be a
  // single action or an array of actions: onPress/onChange/onTick/...).
  const findSetState = (value: unknown): void => {
    if (Array.isArray(value)) { value.forEach(findSetState); return; }
    if (value && typeof value === 'object') {
      const v = value as Record<string, unknown>;
      if (v.type === 'setState' && typeof v.key === 'string') written.add(v.key);
      Object.values(v).forEach(findSetState);
    }
  };
  if (comp.props) findSetState(comp.props);

  // state keys READ inside style (these must be initialized or layout breaks).
  if ((comp as any).style) {
    const refs = new Set<string>();
    collectStateRefs((comp as any).style, refs);
    for (const r of refs) if (!styleReads.has(r)) styleReads.set(r, path);
  }

  for (const child of comp.children ?? []) walk(child, written, styleReads, `${path}.${child.id}`);
}

export function semanticLint(definition: AppDefinition): ValidationError[] {
  const errors: ValidationError[] = [];
  const screens = definition.screens ?? [];

  // --- 1. State read in `style` but never initialized via setState → NaN layout ---
  const written = new Set<string>();
  const styleReads = new Map<string, string>();
  for (const screen of screens) {
    for (const comp of screen.components ?? []) walk(comp, written, styleReads, `screen.${screen.id}.${comp.id}`);
  }
  for (const [key, path] of styleReads) {
    if (!written.has(key)) {
      errors.push({
        path,
        message: `Layout uses $.state.${key} but no setState ever initializes "${key}", so it is undefined → NaN and the element won't position/show. Initialize it (e.g. a setState on the start/onPress action, or reset action) before it's read in style.`,
      });
    }
  }

  // --- 2. Unreachable screens (nothing navigates to them) ---
  if (screens.length > 1) {
    const navTargets = new Set<string>();
    (definition.navigation?.screens ?? []).forEach((n) => navTargets.add(n.screenId));
    const findNav = (value: unknown): void => {
      if (Array.isArray(value)) { value.forEach(findNav); return; }
      if (value && typeof value === 'object') {
        const v = value as Record<string, unknown>;
        if (v.type === 'navigate' && typeof v.screen === 'string') navTargets.add(v.screen);
        Object.values(v).forEach(findNav);
      }
    };
    for (const screen of screens) for (const comp of screen.components ?? []) findNav(comp.props);
    // The first screen is the entry point; tab/nav screens and navigate targets are reachable.
    const entry = screens[0]?.id;
    for (const screen of screens) {
      if (screen.id !== entry && !navTargets.has(screen.id)) {
        errors.push({
          path: `screen.${screen.id}`,
          message: `Screen "${screen.id}" is unreachable — it's not in navigation and no navigate action targets it. Add it to navigation or a navigate action, or remove it.`,
        });
      }
    }
  }

  // --- 3. A top-level list with no emptyComponent → a blank/placeholder gap when empty ---
  // UI/UX: every list needs a designed empty state (see docs/ui-ux-principles.md). Flag lists
  // that render primary content but lack one so the auto-fix adds it. (Nested lists inside a
  // renderItem are skipped — those are per-row detail, not primary content.)
  for (const screen of screens) {
    for (const comp of screen.components ?? []) {
      forEachTopLevelList(comp, (list, path) => {
        const p = (list.props ?? {}) as Record<string, unknown>;
        const topEmpty = (list as unknown as { emptyComponent?: unknown }).emptyComponent;
        if (p.emptyComponent === undefined && topEmpty === undefined) {
          errors.push({
            path,
            message: `List "${list.id}" has no emptyComponent — when it has no data it shows a blank/placeholder gap. Add an emptyComponent (an emptyState with an icon, a friendly title, a one-line subtitle, and an action that navigates to where the user adds data).`,
          });
        }
      }, `screen.${screen.id}.${comp.id}`);
    }
  }

  // --- 4. Aggregate / filter over a field that does NOT exist on the entity ---
  // sum/avg/min/max/pluck($.data.X, 'field') or where($.data.X, 'field', …) where `field` isn't a
  // known key of X → the aggregate is always 0/blank or the filter matches nothing. This is the
  // "$0 Revenue" class: aggregating a cross-entity value (e.g. a service `price`) that was never
  // denormalized onto the row. Guards (prefer UNDER-flagging — a false positive triggers a
  // damaging full-app repair pass): skip health-source entities, keys that are seeded or written
  // by a form/action (denormalized values), `_`-prefixed/platform keys + `id`, and any entity we
  // can't resolve to a schema.
  const knownKeys = buildKnownKeys(definition);
  const healthEntities = new Set<string>();
  for (const s of definition.schemas ?? []) if (s.source === 'health') healthEntities.add(s.name);
  const seen = new Set<string>();
  const checkFieldRef = (entity: string, field: string, kind: 'aggregate' | 'filter', fn: string, path: string): void => {
    const keys = knownKeys.get(entity);
    if (!keys) return;                        // unresolvable entity → skip
    if (healthEntities.has(entity)) return;   // device-sourced fields aren't declared in the schema
    if (field.startsWith('_') || field === 'id') return; // platform/implicit keys
    if (keys.has(field)) return;              // known (schema / seed / denormalized) → ok
    const dedupe = `${entity}|${field}|${kind}`;
    if (seen.has(dedupe)) return;
    seen.add(dedupe);
    errors.push({
      path,
      message: kind === 'aggregate'
        ? `${fn}(...) aggregates field '${field}' which is not a field on entity '${entity}', so it is always 0/blank. If '${field}' comes from another entity (e.g. a service price), DENORMALIZE it onto '${entity}' at write time (copy it into the submitForm/updateItem data) and add it to the schema; otherwise aggregate an existing field.`
        : `where(...) filters entity '${entity}' by field '${field}' which does not exist on it, so the filter matches nothing. Add '${field}' to the '${entity}' schema or filter by an existing field.`,
    });
  };
  for (const screen of screens) {
    const strings: string[] = [];
    for (const comp of screen.components ?? []) collectStrings(comp.props, strings);
    for (const str of strings) {
      for (const call of extractFieldCalls(str)) {
        checkFieldRef(call.entity, call.field, call.kind, call.fn, `screen.${screen.id}`);
      }
    }
  }

  // --- 5. Calls to a function that isn't in the engine's builtin registry ---
  // A model can hallucinate a plausible-sounding function (toDateKey(), currentStreakLabel())
  // that isn't in src/engine/expression/functions.ts. Nothing else catches this: validateDefinition
  // doesn't know about expressions, and ExpressionEngine.evaluate wraps evaluation in
  // try/catch → undefined, so the thrown "Unknown function" is swallowed and the bound UI
  // silently renders blank/zero — no error surfaces anywhere until a human notices. Gate on
  // the SAME isExpression() the runtime uses before ever tokenizing a string, so plain prose
  // that merely looks like a call ("Rate (1-5)", "take (a break)", "Complete (optional)")
  // is never touched. Prefer under-flagging: a parse failure or any doubt → skip, don't guess.
  const unknownCallSeen = new Set<string>();
  const flagUnknownCalls = (comp: ComponentDefinition, path: string): void => {
    const strings: string[] = [];
    collectStrings(comp.props, strings);
    if (comp.style) collectStrings(comp.style, strings);
    for (const str of strings) {
      if (!expressionEngine.isExpression(str)) continue; // not something the engine would ever evaluate
      let ast: ExpressionNode;
      try {
        ast = parse(tokenize(str));
      } catch {
        continue; // unparseable — don't guess at intent, don't flag
      }
      const calls: string[] = [];
      collectFunctionCalls(ast, calls);
      for (const name of calls) {
        if (BUILTIN_NAMES.has(name)) continue;
        const dedupe = `${path}|${name}`;
        if (unknownCallSeen.has(dedupe)) continue;
        unknownCallSeen.add(dedupe);
        errors.push({
          path,
          message: `Calls "${name}(...)" which is not a registered engine function (see src/engine/expression/functions.ts for the full list). At runtime this throws "Unknown function: ${name}", which ExpressionEngine swallows into undefined — the bound value silently renders blank/zero. Use an existing builtin, or compose the result from ones that exist (e.g. formatDate/addDays/diffDays for date keys, or a where()+count() pair for a derived label) instead of inventing a new one.`,
        });
      }
    }
    for (const child of comp.children ?? []) flagUnknownCalls(child, `${path}.${child.id}`);
  };
  for (const screen of screens) {
    for (const comp of screen.components ?? []) flagUnknownCalls(comp, `screen.${screen.id}.${comp.id}`);
  }

  // --- 6. References to entities/fields that provably do not exist ---
  // Same root cause as check #5 (a plausible-looking reference the model invents, swallowed by
  // ExpressionEngine's try/catch into `undefined` with zero visible error), but for DATA shape
  // instead of function names. Real-world case that motivated this: a grid cell colored by
  // `$.item.__color` where the grid's `data` was `range(56)` — $.item is a plain NUMBER there, so
  // the property access always resolves to undefined and every cell renders the same fallback
  // color. Kept intentionally narrow (a false positive here drives an automated repair pass to
  // rewrite a WORKING app, which is worse than under-flagging). Three sub-cases, all requiring the
  // binding to be PROVABLE from the definition itself — anything else (pluck()'s result type
  // depends on the plucked field; $.state/$.params/$.user; a nested list keyed off the outer
  // $.item; an unparseable expression) is left alone:
  //   (a) $.data.<entity> naming an entity with no schema at all.
  //   (b) $.data.<entity>.<field> — every entity is a LIST of rows (see
  //       PreviewDataSource.list()/BackendDataSource), so dot-accessing anything but `.length`
  //       directly on it is wrong regardless of whether <field> happens to match a real field
  //       name on the entity (you can't get a row without an index/first()/where()).
  //   (c) $.item.<field> where the enclosing list/grid's `data` resolves to `$.data.<entity>` (or
  //       a row-preserving where()/sortBy() over it) — field must be a known key of that entity —
  //       or to `range(...)` — provably plain numbers, so ANY property access is wrong.
  const entityNames = new Set(knownKeys.keys());
  const fieldRefSeen = new Set<string>();
  const emitFieldRef = (path: string, message: string): void => {
    const dedupe = `${path}|${message}`;
    if (fieldRefSeen.has(dedupe)) return;
    fieldRefSeen.add(dedupe);
    errors.push({ path, message });
  };

  /** $.data.<entity>[.<field>] anywhere in a string — entity-existence and the "an array isn't a
   *  row" check are unconditional facts about the schema, independent of where the string lives
   *  (no list/grid scoping needed, unlike $.item below). */
  const checkDataEntityRefs = (str: string, path: string): void => {
    if (!expressionEngine.isExpression(str)) return;
    let ast: ExpressionNode;
    try { ast = parse(tokenize(str)); } catch { return; }
    const visit = (node: ExpressionNode | string | null | undefined): void => {
      if (!node || typeof node !== 'object') return;
      if (node.type === ASTNodeType.MemberAccess) {
        const chain = dollarChain(node);
        if (chain && chain[0] === 'data' && chain.length >= 2) {
          const entity = chain[1];
          if (!entityNames.has(entity)) {
            emitFieldRef(path, `References entity '${entity}' via $.data.${entity}, but no schema named '${entity}' is declared (declared schemas: ${[...entityNames].join(', ') || 'none'}). This always evaluates to undefined — use a declared schema name or add the '${entity}' schema.`);
            return;
          }
          if (chain.length >= 3 && chain[2] !== 'length') {
            emitFieldRef(path, `$.data.${entity}.${chain[2]} accesses a property directly on the '${entity}' array, but $.data.${entity} is a LIST of rows (arrays only have .length here) — this always evaluates to undefined. Resolve a single row first, e.g. first(where($.data.${entity}, 'id', <id-expr>)).${chain[2]}.`);
            return;
          }
          return; // $.data.<entity> or $.data.<entity>.length — nothing more to check in this chain
        }
        visit(node.object as ExpressionNode);
        return;
      }
      walkExprChildren(node, visit);
    };
    visit(ast);
  };

  /** $.item.<field> where the enclosing list/grid's `data` resolves (via resolveItemType below)
   *  to a known entity's rows or to provably-primitive values. `itemType` is threaded down the
   *  component tree by walkFieldRefScopes, changing only at a list/grid boundary. */
  const checkItemFieldRefs = (str: string, itemType: ResolvedItemType, path: string): void => {
    if (itemType.kind === 'unknown') return; // can't prove the binding — never flag
    if (!expressionEngine.isExpression(str)) return;
    let ast: ExpressionNode;
    try { ast = parse(tokenize(str)); } catch { return; }
    const visit = (node: ExpressionNode | string | null | undefined): void => {
      if (!node || typeof node !== 'object') return;
      if (node.type === ASTNodeType.MemberAccess) {
        const objChain = dollarChain(node.object as ExpressionNode);
        if (objChain && objChain.length === 1 && objChain[0] === 'item') {
          const field = node.property;
          if (itemType.kind === 'primitive') {
            emitFieldRef(path, `$.item.${field} is accessed here, but this list/grid's data ("${itemType.source}") resolves to plain values, not rows — $.item has no properties, so this always evaluates to undefined (e.g. a blank/transparent cell or a "0" that never changes). Bind data to an entity ($.data.<entity>) for per-row fields, or remove the property access.`);
          } else {
            const keys = knownKeys.get(itemType.entity);
            if (keys && !keys.has(field)) {
              emitFieldRef(path, `$.item.${field} is accessed here, but the enclosing list/grid's data is $.data.${itemType.entity} rows, and '${field}' is not a field on entity '${itemType.entity}'. This always evaluates to undefined. Use one of its declared fields, or add '${field}' to the '${itemType.entity}' schema.`);
            }
          }
          return; // $.item itself has nothing further worth walking
        }
        visit(node.object as ExpressionNode);
        return;
      }
      walkExprChildren(node, visit);
    };
    visit(ast);
  };

  /** Walk a component tree threading the current $.item binding, which changes only at a
   *  list/grid boundary — `renderItem` gets the type its OWN `data` resolves to; every other prop
   *  /style on the list/grid (including `emptyComponent`, which has no per-item scope at all,
   *  since it renders exactly when there ISN'T an item) is checked against the OUTER binding. */
  const walkFieldRefScopes = (comp: ComponentDefinition, itemType: ResolvedItemType, path: string): void => {
    const props = (comp.props ?? {}) as Record<string, unknown>;
    const isListOrGrid = comp.type === 'list' || comp.type === 'grid';
    const renderItem = isListOrGrid ? (props.renderItem as ComponentDefinition | undefined) : undefined;

    const ownStrings: string[] = [];
    for (const [key, value] of Object.entries(props)) {
      if (key === 'renderItem') continue; // scoped separately below, with the CHILD binding
      collectStrings(value, ownStrings);
    }
    if (comp.style) collectStrings(comp.style, ownStrings);
    for (const str of ownStrings) {
      checkDataEntityRefs(str, path);
      checkItemFieldRefs(str, itemType, path);
    }

    const childItemType = isListOrGrid ? resolveItemType(props.data, knownKeys) : itemType;
    if (renderItem) walkFieldRefScopes(renderItem, childItemType, `${path}.renderItem`);
    for (const child of comp.children ?? []) walkFieldRefScopes(child, itemType, `${path}.${child.id}`);
  };
  for (const screen of screens) {
    for (const comp of screen.components ?? []) walkFieldRefScopes(comp, { kind: 'unknown' }, `screen.${screen.id}.${comp.id}`);
  }

  return errors;
}

/** Recursively collect the `name` of every FunctionCall node in an expression AST — including
 *  ones nested in argument positions (round(bogusFn(x), 2)), template interpolations, and
 *  both branches of a conditional. MemberAccess/IndexAccess are walked into (their object/index
 *  may itself contain a call) but never themselves treated as calls — this grammar has no
 *  method-call node type (`obj.method()` is not supported by parser.ts), so a FunctionCall node
 *  is unambiguously a standalone builtin invocation, never `.length`/`.map` style property access. */
function collectFunctionCalls(node: ExpressionNode | string | null | undefined, out: string[]): void {
  if (!node || typeof node !== 'object') return;
  switch (node.type) {
    case ASTNodeType.FunctionCall: {
      out.push(node.name);
      for (const arg of node.args ?? []) collectFunctionCalls(arg as ExpressionNode, out);
      break;
    }
    case ASTNodeType.MemberAccess:
      collectFunctionCalls(node.object as ExpressionNode, out);
      break;
    case ASTNodeType.IndexAccess:
      collectFunctionCalls(node.object as ExpressionNode, out);
      collectFunctionCalls(node.index as ExpressionNode, out);
      break;
    case ASTNodeType.BinaryOp:
      collectFunctionCalls(node.left as ExpressionNode, out);
      collectFunctionCalls(node.right as ExpressionNode, out);
      break;
    case ASTNodeType.UnaryOp:
      collectFunctionCalls(node.operand as ExpressionNode, out);
      break;
    case ASTNodeType.Conditional:
      collectFunctionCalls(node.condition as ExpressionNode, out);
      collectFunctionCalls(node.consequent as ExpressionNode, out);
      collectFunctionCalls(node.alternate as ExpressionNode, out);
      break;
    case ASTNodeType.ArrayLiteral:
      for (const el of node.elements ?? []) collectFunctionCalls(el as ExpressionNode, out);
      break;
    case ASTNodeType.ObjectLiteral:
      for (const prop of (node as any).properties ?? []) collectFunctionCalls(prop.value as ExpressionNode, out);
      break;
    case ASTNodeType.TemplateString:
      for (const part of node.parts ?? []) if (typeof part !== 'string') collectFunctionCalls(part as ExpressionNode, out);
      break;
    // Literal, Identifier → leaf nodes, no children to walk.
    default:
      break;
  }
}

/** Visit every child of `node` other than a MemberAccess's `.object` (callers of this handle
 *  MemberAccess themselves, since whether to descend into `.object` depends on whether the node
 *  itself was already a recognized $-chain). Shared by check #6's two AST walkers below — same
 *  node coverage as collectFunctionCalls, generalized to an arbitrary visitor. */
function walkExprChildren(node: ExpressionNode, visit: (n: ExpressionNode | string | null | undefined) => void): void {
  switch (node.type) {
    case ASTNodeType.IndexAccess:
      visit(node.object as ExpressionNode);
      visit(node.index as ExpressionNode);
      break;
    case ASTNodeType.FunctionCall:
      for (const arg of node.args ?? []) visit(arg as ExpressionNode);
      break;
    case ASTNodeType.BinaryOp:
      visit(node.left as ExpressionNode);
      visit(node.right as ExpressionNode);
      break;
    case ASTNodeType.UnaryOp:
      visit(node.operand as ExpressionNode);
      break;
    case ASTNodeType.Conditional:
      visit(node.condition as ExpressionNode);
      visit(node.consequent as ExpressionNode);
      visit(node.alternate as ExpressionNode);
      break;
    case ASTNodeType.ArrayLiteral:
      for (const el of node.elements ?? []) visit(el as ExpressionNode);
      break;
    case ASTNodeType.ObjectLiteral:
      for (const prop of (node as any).properties ?? []) visit(prop.value as ExpressionNode);
      break;
    case ASTNodeType.TemplateString:
      for (const part of node.parts ?? []) if (typeof part !== 'string') visit(part as ExpressionNode);
      break;
    // MemberAccess handled by the caller; Literal/Identifier → leaf nodes.
    default:
      break;
  }
}

/** Follow a chain of MemberAccess nodes back to the `$` root identifier, returning the property
 *  path in order — e.g. `$.data.appts.status` → ['data','appts','status']; `$.item` → ['item'].
 *  Returns null when the chain does NOT bottom out at `$` (e.g. `first(...).field`, `$.item` is
 *  fine but `foo.item` or an IndexAccess breaks the chain) — those are exactly the shapes we
 *  cannot safely reason about, so callers treat null as "not a $-reference, skip". */
function dollarChain(node: ExpressionNode | string | null | undefined): string[] | null {
  const parts: string[] = [];
  let cur: ExpressionNode | null = (node && typeof node === 'object') ? node : null;
  while (cur && cur.type === ASTNodeType.MemberAccess) {
    parts.unshift(cur.property);
    cur = cur.object as ExpressionNode;
  }
  if (cur && cur.type === ASTNodeType.Identifier && cur.name === '$') return parts;
  return null;
}

/** What a list/grid's `data` expression resolves to, for checking $.item.<field> inside its
 *  renderItem. Kept deliberately conservative — see the check #6 doc comment above for why. */
type ResolvedItemType =
  | { kind: 'entity'; entity: string }
  | { kind: 'primitive'; source: string }
  | { kind: 'unknown' };

/** Resolve a list/grid's `data` prop to what $.item will be inside its renderItem:
 *   - `$.data.<entity>` for a KNOWN schema → rows of that entity (fields = the schema's keys).
 *   - `where(X, ...)` / `sortBy(X, ...)` → row-preserving; resolve recursively through X.
 *   - `range(...)` → PROVABLY plain numbers (see functions.ts) — item has no properties at all.
 *   - anything else — `pluck()` (return type depends on the plucked field, could be an
 *     object/array field), `$.state`/`$.params`, a nested list keyed off the OUTER $.item, an
 *     unresolvable entity, or a parse failure — is 'unknown' and never flagged. */
function resolveItemType(data: unknown, knownKeys: Map<string, Set<string>>): ResolvedItemType {
  if (typeof data !== 'string' || !expressionEngine.isExpression(data)) return { kind: 'unknown' };
  let ast: ExpressionNode;
  try { ast = parse(tokenize(data)); } catch { return { kind: 'unknown' }; }
  return resolveNodeType(ast, knownKeys, data);
}

function resolveNodeType(node: ExpressionNode, knownKeys: Map<string, Set<string>>, source: string): ResolvedItemType {
  if (node.type === ASTNodeType.TemplateString) {
    const parts = node.parts ?? [];
    const exprParts = parts.filter((p) => typeof p !== 'string');
    // Only a template that is ENTIRELY a single interpolation (no surrounding text) is safe to
    // unwrap — "${...}" as the whole string, not "prefix ${...}".
    if (parts.length === 1 && exprParts.length === 1) return resolveNodeType(exprParts[0] as ExpressionNode, knownKeys, source);
    return { kind: 'unknown' };
  }
  const chain = dollarChain(node);
  if (chain && chain[0] === 'data' && chain.length === 2) {
    const entity = chain[1];
    return knownKeys.has(entity) ? { kind: 'entity', entity } : { kind: 'unknown' }; // unknown-entity is reported by checkDataEntityRefs, not here
  }
  if (node.type === ASTNodeType.FunctionCall) {
    if (node.name === 'range') return { kind: 'primitive', source };
    if ((node.name === 'where' || node.name === 'sortBy') && (node.args ?? []).length > 0) {
      return resolveNodeType(node.args[0] as ExpressionNode, knownKeys, source);
    }
  }
  return { kind: 'unknown' };
}

/** entity → set of keys that legitimately exist on it: schema fields + ownerField + seedData keys
 *  + any key written by a form bind or a submitForm/updateItem/createItem action `data` (i.e.
 *  denormalized values). Used to decide whether an aggregated/filtered field really exists. */
function buildKnownKeys(definition: AppDefinition): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const s of (definition.schemas ?? []) as EntitySchema[]) {
    const set = new Set<string>(['id']);
    for (const f of s.fields ?? []) set.add(f.name);
    if (s.ownerField) set.add(s.ownerField);
    for (const row of s.seedData ?? []) for (const k of Object.keys(row ?? {})) set.add(k);
    map.set(s.name, set);
  }
  const add = (entity: string, key: string): void => { map.get(entity)?.add(key); };
  const walkWrites = (comp: ComponentDefinition, formEntity: string | null): void => {
    const props = (comp.props ?? {}) as Record<string, unknown>;
    const myForm = comp.type === 'form' && typeof props.entity === 'string' ? props.entity : formEntity;
    if (typeof props.bind === 'string' && myForm) add(myForm, props.bind);
    scanActionWrites(props, add);
    for (const child of comp.children ?? []) walkWrites(child, myForm);
  };
  for (const screen of definition.screens ?? []) for (const comp of screen.components ?? []) walkWrites(comp, null);
  return map;
}

const WRITE_ACTIONS = new Set(['submitForm', 'createItem', 'updateItem', 'upsertItem', 'saveItem']);
/** Collect keys written by any create/update action `data` object, anywhere in a props tree. */
function scanActionWrites(value: unknown, add: (entity: string, key: string) => void): void {
  if (Array.isArray(value)) { value.forEach((v) => scanActionWrites(v, add)); return; }
  if (value && typeof value === 'object') {
    const v = value as Record<string, unknown>;
    if (typeof v.type === 'string' && WRITE_ACTIONS.has(v.type) && typeof v.entity === 'string'
      && v.data && typeof v.data === 'object') {
      for (const k of Object.keys(v.data as Record<string, unknown>)) add(v.entity, k);
    }
    Object.values(v).forEach((x) => scanActionWrites(x, add));
  }
}

/** Gather every string value in a props subtree (expressions live in strings). */
function collectStrings(value: unknown, out: string[]): void {
  if (typeof value === 'string') { out.push(value); return; }
  if (Array.isArray(value)) { value.forEach((v) => collectStrings(v, out)); return; }
  if (value && typeof value === 'object') for (const v of Object.values(value)) collectStrings(v, out);
}

interface FieldCall { entity: string; field: string; kind: 'aggregate' | 'filter'; fn: string; }

/** Parse an expression string for aggregate/filter calls, extracting the entity ($.data.X the
 *  array flows from) and the field arg, so we can check the field exists. Balanced-paren aware so
 *  nested forms like sum(where($.data.x,'s','c'),'price') resolve the sum's field as 'price'. */
function extractFieldCalls(expr: string): FieldCall[] {
  const out: FieldCall[] = [];
  const AGG = /\b(sum|avg|min|max|pluck)\s*\(/g;
  let m: RegExpExecArray | null;
  while ((m = AGG.exec(expr))) {
    const { inner } = extractBalanced(expr, m.index + m[0].length - 1);
    const args = splitTopLevel(inner);
    if (args.length < 2) continue;                    // no field arg (e.g. summing numbers)
    const field = literalString(args[args.length - 1]);
    if (!field) continue;                             // field isn't a plain string literal → skip
    const entity = firstDataEntity(inner);
    if (entity) out.push({ entity, field, kind: 'aggregate', fn: m[1] });
  }
  const WHERE = /\bwhere\s*\(/g;
  while ((m = WHERE.exec(expr))) {
    const { inner } = extractBalanced(expr, m.index + m[0].length - 1);
    const args = splitTopLevel(inner);
    if (args.length < 2) continue;
    const field = cleanFieldName(literalString(args[1]));  // where(array, field, …)
    if (!field) continue;                                  // operator/reordered/non-literal → skip
    const entity = firstDataEntity(args[0]);               // entity flows from the array arg
    if (entity) out.push({ entity, field, kind: 'filter', fn: 'where' });
  }
  return out;
}

/** Given the index of an opening '(', return the substring between it and its matching ')'. */
function extractBalanced(s: string, openIdx: number): { inner: string; end: number } {
  let depth = 0;
  for (let i = openIdx; i < s.length; i++) {
    if (s[i] === '(') depth++;
    else if (s[i] === ')') { depth--; if (depth === 0) return { inner: s.slice(openIdx + 1, i), end: i + 1 }; }
  }
  return { inner: s.slice(openIdx + 1), end: s.length };
}

/** Split call arguments on top-level commas (ignoring commas nested in parens/quotes). */
function splitTopLevel(inner: string): string[] {
  const args: string[] = [];
  let depth = 0, start = 0, quote = '';
  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i];
    if (quote) { if (ch === quote) quote = ''; continue; }
    if (ch === "'" || ch === '"') quote = ch;
    else if (ch === '(') depth++;
    else if (ch === ')') depth--;
    else if (ch === ',' && depth === 0) { args.push(inner.slice(start, i).trim()); start = i + 1; }
  }
  args.push(inner.slice(start).trim());
  return args;
}

/** If an arg is a quoted string literal, return its contents; otherwise null. */
function literalString(arg: string): string | null {
  const t = arg.trim();
  const m = /^'([^']*)'$/.exec(t) ?? /^"([^"]*)"$/.exec(t);
  return m ? m[1] : null;
}

/** Reduce a where() field arg to a bare field name (handles the glued-operator form 'price > 0');
 *  returns null when it isn't a plain identifier (e.g. it's actually an operator token). */
function cleanFieldName(field: string | null): string | null {
  if (!field) return null;
  const m = /^([a-zA-Z_]\w*)/.exec(field.trim());
  return m ? m[1] : null;
}

/** The first $.data.<entity> referenced in an expression fragment. */
function firstDataEntity(fragment: string): string | null {
  const m = /\$\.data\.([a-zA-Z_]\w*)/.exec(fragment);
  return m ? m[1] : null;
}

/** Visit every list that is NOT inside another list's renderItem (i.e. primary-content lists). */
function forEachTopLevelList(
  comp: ComponentDefinition,
  fn: (list: ComponentDefinition, path: string) => void,
  path: string,
): void {
  if (comp.type === 'list') {
    fn(comp, path);
    // Its renderItem's own nested lists are per-row detail — don't recurse into them.
    for (const child of comp.children ?? []) forEachTopLevelList(child, fn, `${path}.${child.id}`);
    return;
  }
  for (const child of comp.children ?? []) forEachTopLevelList(child, fn, `${path}.${child.id}`);
}
